/**
 * The lead board — `/`, the app's home screen. Three summary tiles, search,
 * stage filter chips (plus Archived), and the lead grid. Read-only: stage
 * moves, archive and restore live on the detail screen (build item 11).
 *
 * Reads leads from the offline mirror + this device's unsynced drafts via
 * `lib/board-data.ts`'s `useBoardLeads()`, so the board works fully offline —
 * see that module's doc comment for the merge and for the current
 * archived-leads limitation. Filtering, search and the summary tiles follow
 * the signed-off design mock exactly (`docs/design/Lead Manager.dc.html`,
 * `renderVals()`): the all-events `Select` (session `currentEventId`) narrows
 * BEFORE the summary totals and search are computed; search then narrows the
 * stage-chip counts; the stage/Archived chip narrows what is actually listed.
 */
import { formatMoneyWhole, LeadStage } from '@paradise/shared';
import { createFileRoute, Link, useRouterState } from '@tanstack/react-router';
import { Printer } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';

import { LeadGrid } from '@/components/board/lead-grid';
import { StageChips, type BoardStageFilter } from '@/components/board/stage-chips';
import { FollowUpsBanner } from '@/components/board/follow-ups-banner';
import { PendingRequestsBanner } from '@/components/board/pending-requests-banner';
import { SummaryTiles } from '@/components/board/summary-tiles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useSessionStore } from '@/lib/auth/session';
import { computeBoardSummary, useArchivedLeadsQuery, useBoardLeads, type BoardLead } from '@/lib/board-data';
import { useEventsQuery } from '@/lib/events/use-events-query';
import { useIsOffline } from '@/lib/offline';
import { useRestoreListScroll } from '@/lib/use-restore-list-scroll';
import { STAGE_ORDER } from '@/lib/stages';
import { useDebouncedSearchParam } from '@/lib/use-debounced-search-param';

/**
 * Board filter state lives in the URL, not in `useState`. Opening a lead pushes
 * a history entry, so Back restores this URL — which is what stops the stage
 * filter resetting to All every time someone looks at a buyer.
 *
 * Every field is optional so the existing `navigate({ to: '/' })` call sites
 * stay valid, and `.catch` so a hand-mangled URL falls back to the default
 * instead of throwing the error screen at a booth.
 */
const BoardSearch = z.object({
  stage: z
    .union([LeadStage, z.literal('all'), z.literal('archived')])
    .optional()
    .catch(undefined),
  q: z.string().optional().catch(undefined)
});

export const Route = createFileRoute('/_app/')({
  // Board is the design's `isListView` app bar with `barSub: 'My Buyers'` and
  // the all-events `Select` (`isBoard`-only) — see lib/router/static-data.ts.
  staticData: { barSubtitle: 'My Buyers', showEventSelect: true },
  validateSearch: BoardSearch,
  component: Board
});

// Every row is 'INR' today (packages/shared/src/money.ts), but the summary
// tiles aggregate ACROSS leads, so there is no single row's `currency` field
// to defer to — `formatMoneyWhole` still requires one explicitly.
const DISPLAY_CURRENCY = 'INR';

function matchesQuery(lead: BoardLead, query: string): boolean {
  if (query.length === 0) return true;
  const haystack = [
    lead.company,
    lead.contactName,
    lead.city,
    lead.country,
    lead.email,
    lead.phone,
    lead.notes,
    lead.ref !== null ? String(lead.ref) : null
  ];
  return haystack.some(value => value !== null && value.toLowerCase().includes(query));
}

function formatSyncTime(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function Board() {
  // `useBoardLeads()`'s own `archivedLeads` is the offline-mirror-derived
  // shape (always empty — see board-data.ts's doc comment); the board's real
  // archived list is the online-only `useArchivedLeadsQuery` below.
  const { leads, lastSyncAt, isLoading } = useBoardLeads();
  const { data: events } = useEventsQuery();
  const currentEventId = useSessionStore(state => state.currentEventId);
  const isOffline = useIsOffline();

  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();

  const stageFilter: BoardStageFilter = searchParams.stage ?? 'all';

  // `replace` rather than push: tapping four chips in a row should not mean four
  // presses of Back to leave the board.
  function setStageFilter(next: BoardStageFilter): void {
    void navigate({ search: prev => ({ ...prev, stage: next === 'all' ? undefined : next }), replace: true });
  }

  const [search, setSearch] = useDebouncedSearchParam(searchParams.q ?? '', next => {
    void navigate({ search: prev => ({ ...prev, q: next === '' ? undefined : next }), replace: true });
  });

  // Fetched only while the Archived chip is the active filter, and only when
  // online — archived leads are never precached (docs/plan.md, "Reading
  // existing leads offline" covers active leads only).
  const archivedQuery = useArchivedLeadsQuery(stageFilter === 'archived' && !isOffline);
  const archivedLeads = useMemo(() => archivedQuery.data ?? [], [archivedQuery.data]);

  // The app bar's all-events Select narrows the whole screen — tiles, chip
  // counts and the list — before anything else, matching the design mock.
  const eventFiltered = useMemo(
    () => (currentEventId ? leads.filter(lead => lead.eventId === currentEventId) : leads),
    [leads, currentEventId]
  );
  const archivedEventFiltered = useMemo(
    () => (currentEventId ? archivedLeads.filter(lead => lead.eventId === currentEventId) : archivedLeads),
    [archivedLeads, currentEventId]
  );

  const summary = useMemo(() => computeBoardSummary(eventFiltered), [eventFiltered]);

  const query = search.trim().toLowerCase();
  const searched = useMemo(() => eventFiltered.filter(lead => matchesQuery(lead, query)), [eventFiltered, query]);

  const stageCounts = useMemo(() => {
    const counts = Object.fromEntries(STAGE_ORDER.map(stage => [stage, 0])) as Record<LeadStage, number>;
    for (const lead of searched) counts[lead.stage] += 1;
    return counts;
  }, [searched]);

  const listLeads = useMemo(() => {
    if (stageFilter === 'archived') return archivedEventFiltered.filter(lead => matchesQuery(lead, query));
    if (stageFilter === 'all') return searched;
    return searched.filter(lead => lead.stage === stageFilter);
  }, [stageFilter, searched, archivedEventFiltered, query]);

  const showingArchivedOffline = stageFilter === 'archived' && isOffline;
  const listIsLoading = isLoading || (stageFilter === 'archived' && archivedQuery.isLoading);

  // Opening a buyer and coming back must land where the user left. `!listIsLoading`
  // is the "the list has rendered and <main> has height" signal — the router's own
  // restoration fires too early to wait for it (lib/use-restore-list-scroll.ts).
  useRestoreListScroll(!listIsLoading);
  const historyKey = useRouterState({ select: state => state.location.state.__TSR_key });

  return (
    <div className="mx-auto flex w-full max-w-220 flex-col gap-2.5 p-3 sm:gap-4 sm:p-4 lg:max-w-7xl">
      <SummaryTiles
        buyerCount={summary.buyerCount}
        totalValueLabel={formatMoneyWhole(summary.totalValue, DISPLAY_CURRENCY)}
        advancePaidLabel={formatMoneyWhole(summary.advancePaid, DISPLAY_CURRENCY)}
      />

      <PendingRequestsBanner />

      {/* `leads`, not `eventFiltered` — the queue is not event-scoped, and a
          banner counting less than the screen it opens would be a lie. */}
      <FollowUpsBanner leads={leads} />

      <div className="flex items-center gap-2">
        <Input
          placeholder="Search by name, company, city or notes…"
          value={search}
          onChange={event => setSearch(event.target.value)}
          aria-label="Search buyers"
          className="flex-1"
        />
        <Button variant="outline" size="sm" className="flex-none" render={<Link to="/reports/leads" />}>
          <Printer />
          <span className="hidden sm:inline">Print report</span>
        </Button>
      </div>

      <StageChips
        value={stageFilter}
        onChange={setStageFilter}
        counts={stageCounts}
        allCount={searched.length}
        archivedCount={archivedQuery.data !== undefined ? archivedEventFiltered.length : null}
      />

      {isOffline && stageFilter !== 'archived' && lastSyncAt !== null && (
        <p className="text-muted-foreground -mt-2 text-xs">Showing leads as of {formatSyncTime(lastSyncAt)}</p>
      )}

      {showingArchivedOffline && (
        <p className="text-muted-foreground -mt-2 text-xs">Archived buyers need a connection to load.</p>
      )}

      {listIsLoading ? (
        <p className="text-muted-foreground mt-4 flex items-center gap-2 text-sm">
          <Spinner className="size-4" aria-hidden="true" /> Loading…
        </p>
      ) : (
        <LeadGrid
          leads={listLeads}
          events={events ?? []}
          resetKey={`${stageFilter}|${query}|${currentEventId ?? 'all'}`}
          restoreKey={historyKey === undefined ? undefined : `board|${historyKey}`}
        />
      )}
    </div>
  );
}
